import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { QRCodeSVG } from 'qrcode.react'
import {
  CODE_LINE_HEIGHT,
  COMPACT_CARD,
  COMPACT_CODE_BOX,
  COMPACT_CREDENTIAL_BOX,
  COMPACT_LOOSE_CODE_BOX,
  COMPACT_LOOSE_CREDENTIAL_BOX,
  CREDENTIAL_LABEL,
  PAGE,
  STANDARD_CODE_BOX,
  STANDARD_CREDENTIAL_BOX,
  STANDARD_CREDENTIAL_GAP_MM,
  cutLines,
  fitCode,
  paginate,
  qrContent,
  serialLabel,
  sheetMargins,
  type SheetLayout,
} from './voucherPrintLayout'
import { PRINT_ROOT } from './printReady'

// Admin va partner panellarida bayt-bayt bir xil (tests/voucher-print.mjs).

export interface PrintCard {
  serialNo: number
  /** Login va parolli kartada — login */
  code: string
  /** Faqat login va parolli kartada */
  password: string | null
  qrToken: string | null
}

/** Kartaga chiqadigan, allaqachon tarjima va formatlangan yozuvlar */
export interface CardMeta {
  batchNo: number
  quantity: number
  /** Karta berilgan manzil/joy (partiya izohi) */
  note: string | null
  planName: string
  price: string
  validUntil: string | null
  issuer: string | null
  branch: string | null
  logoUrl: string | null
  /** Partiya yoki tashkilot yo'riqnomasi; bo'lmasa umumiy maslahat */
  instructions: string | null
  hint: string
  portalUrl: string | null
  /** `false` — partiya QR'siz: QR bloki chizilmaydi, kod kattaroq chiqadi */
  printQr: boolean
  loginLabel: string
  passwordLabel: string
}

const PRINT_CSS = `
  .${PRINT_ROOT} { display: none; }
  @media print {
    @page { size: A4 portrait; margin: 0; }
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    body > *:not(.${PRINT_ROOT}) { display: none !important; }
    .${PRINT_ROOT} { display: block; }
    .xsib-voucher-page { break-after: page; page-break-after: always; break-inside: avoid; page-break-inside: avoid; }
    .xsib-voucher-page:last-child { break-after: auto; page-break-after: auto; }
  }
`

/**
 * Chop etishda faqat varaqlar qoladi: ular `body` ning bevosita bolasi,
 * panel va oyna yashiriladi. Qoidalar komponent bilan birga olib
 * tashlanadi — aks holda keyingi har qanday chop etish shu maketga tushardi.
 */
interface SheetProps {
  cards: readonly PrintCard[]
  meta: CardMeta
  layout: SheetLayout
}

export function PrintPages({ cards, meta, layout }: SheetProps) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={PRINT_ROOT}>
      {paginate(cards, layout).map((page) => (
        <SheetPage key={page[0]?.serialNo} cards={page} meta={meta} layout={layout} />
      ))}
    </div>,
    document.body,
  )
}

export function SheetPage({ cards, meta, layout }: SheetProps) {
  const margins = sheetMargins(layout)
  const Card = layout.id === 'compact' ? CompactCard : StandardCard
  return (
    <div
      className="xsib-voucher-page"
      style={{
        position: 'relative',
        width: `${PAGE.widthMm}mm`,
        height: `${PAGE.heightMm}mm`,
        overflow: 'hidden',
        background: '#fff',
        color: '#000',
      }}
    >
      {layout.cut === 'grid' ? <CutGrid layout={layout} /> : <CutMarks layout={layout} />}
      <div
        style={{
          position: 'absolute',
          left: `${margins.x}mm`,
          top: `${margins.y}mm`,
          display: 'grid',
          gridTemplateColumns: `repeat(${layout.columns}, ${layout.cardWidthMm}mm)`,
          gridAutoRows: `${layout.cardHeightMm}mm`,
        }}
      >
        {cards.map((card) => (
          <Card key={card.serialNo} card={card} meta={meta} layout={layout} />
        ))}
      </div>
    </div>
  )
}

const LOGO_HEIGHT_MM = 6
const QR_SIZE_MM = 24
const INSTRUCTION_LINES = 3

const MARK_LENGTH_MM = 5
const MARK_GAP_MM = 2

function CutMarks({ layout }: { layout: SheetLayout }) {
  const { xs, ys } = cutLines(layout)
  const margins = sheetMargins(layout)
  const mark = { position: 'absolute', background: '#000' } as const
  const before = (margin: number) => `${margin - MARK_GAP_MM - MARK_LENGTH_MM}mm`
  const after = (page: number, margin: number) => `${page - margin + MARK_GAP_MM}mm`
  return (
    <>
      {xs.flatMap((x) => [
        <span key={`t${x}`} style={{ ...mark, left: `${x}mm`, top: before(margins.y), width: '0.2mm', height: `${MARK_LENGTH_MM}mm` }} />,
        <span key={`b${x}`} style={{ ...mark, left: `${x}mm`, top: after(PAGE.heightMm, margins.y), width: '0.2mm', height: `${MARK_LENGTH_MM}mm` }} />,
      ])}
      {ys.flatMap((y) => [
        <span key={`l${y}`} style={{ ...mark, top: `${y}mm`, left: before(margins.x), height: '0.2mm', width: `${MARK_LENGTH_MM}mm` }} />,
        <span key={`r${y}`} style={{ ...mark, top: `${y}mm`, left: after(PAGE.widthMm, margins.x), height: '0.2mm', width: `${MARK_LENGTH_MM}mm` }} />,
      ])}
    </>
  )
}

const GRID_LINE_MM = 0.15

function CutGrid({ layout }: { layout: SheetLayout }) {
  const { xs, ys } = cutLines(layout)
  const line = { position: 'absolute', background: '#888' } as const
  const margins = sheetMargins(layout)
  const width = layout.columns * layout.cardWidthMm
  const height = layout.rows * layout.cardHeightMm
  return (
    <>
      {xs.map((x) => (
        <span key={`v${x}`} style={{ ...line, left: `${x - GRID_LINE_MM / 2}mm`, top: `${margins.y}mm`, width: `${GRID_LINE_MM}mm`, height: `${height}mm` }} />
      ))}
      {ys.map((y) => (
        <span key={`h${y}`} style={{ ...line, top: `${y - GRID_LINE_MM / 2}mm`, left: `${margins.x}mm`, height: `${GRID_LINE_MM}mm`, width: `${width}mm` }} />
      ))}
    </>
  )
}

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

const clamp = (lines: number) =>
  ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  }) as const

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

interface CardProps {
  card: PrintCard
  meta: CardMeta
  layout: SheetLayout
}

function StandardCard({ card, meta, layout }: CardProps) {
  return (
    <div
      style={{
        boxSizing: 'border-box',
        width: `${layout.cardWidthMm}mm`,
        height: `${layout.cardHeightMm}mm`,
        padding: '3mm',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.2mm',
        overflow: 'hidden',
        fontSize: '2.5mm',
        lineHeight: 1.25,
      }}
    >
      <div style={{ display: 'flex', gap: '2mm', alignItems: 'center', height: `${LOGO_HEIGHT_MM}mm`, flex: 'none' }}>
        {meta.logoUrl ? (
          <img
            src={meta.logoUrl}
            alt=""
            style={{ height: `${LOGO_HEIGHT_MM}mm`, maxWidth: '22mm', objectFit: 'contain', flex: 'none' }}
          />
        ) : null}
        <span dir="auto" style={{ ...clamp(2), flex: 1, minWidth: 0, fontWeight: 700, fontSize: '2.5mm', lineHeight: 1.2 }}>
          {meta.note ?? ''}
        </span>
        <bdi dir="ltr" style={{ flex: 'none' }}>
          {serialLabel(meta.batchNo, card.serialNo, meta.quantity)}
        </bdi>
      </div>

      <div style={{ display: 'flex', gap: '2.5mm', flex: 1, minHeight: 0 }}>
        {meta.printQr ? (
          <QRCodeSVG
            value={qrContent(meta.portalUrl, card)}
            size={256}
            level="M"
            marginSize={2}
            style={{ width: `${QR_SIZE_MM}mm`, height: `${QR_SIZE_MM}mm`, flex: 'none' }}
          />
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6mm', minWidth: 0, overflow: 'hidden' }}>
          <span dir="auto" style={{ fontWeight: 700, fontSize: '3.1mm', overflow: 'hidden', maxHeight: '7.8mm' }}>
            {meta.planName}
          </span>
          <span style={{ fontWeight: 700, fontSize: '3.6mm' }}>{meta.price}</span>
          {meta.validUntil ? <span style={{ color: '#444' }}>{meta.validUntil}</span> : null}
          {meta.branch ? (
            <span dir="auto" style={{ ...ellipsis, color: '#444' }}>
              {meta.branch}
            </span>
          ) : null}
          {meta.issuer ? (
            <span dir="auto" style={{ ...ellipsis, color: '#444' }}>
              {meta.issuer}
            </span>
          ) : null}
        </div>
      </div>

      <span
        dir="auto"
        style={{
          flex: 'none',
          color: '#333',
          fontSize: '2.2mm',
          lineHeight: 1.2,
          ...clamp(INSTRUCTION_LINES),
        }}
      >
        {meta.instructions ?? meta.hint}
      </span>

      {card.password === null ? (
        <bdi
          dir="ltr"
          style={{
            ...ellipsis,
            display: 'block',
            fontFamily: MONO_FONT,
            fontWeight: 700,
            fontSize: `${fitCode(card.code, STANDARD_CODE_BOX).fontMm}mm`,
            letterSpacing: '0.05em',
            textAlign: 'center',
          }}
        >
          {card.code}
        </bdi>
      ) : (
        <div style={{ display: 'flex', gap: `${STANDARD_CREDENTIAL_GAP_MM}mm`, flex: 'none' }}>
          <CredentialRow
            label={meta.loginLabel}
            value={card.code}
            box={STANDARD_CREDENTIAL_BOX}
            labelMm={CREDENTIAL_LABEL.standardMm}
            labelFontMm={2.2}
          />
          <CredentialRow
            label={meta.passwordLabel}
            value={card.password}
            box={STANDARD_CREDENTIAL_BOX}
            labelMm={CREDENTIAL_LABEL.standardMm}
            labelFontMm={2.2}
          />
        </div>
      )}
    </div>
  )
}

function CompactCard({ card, meta, layout }: CardProps) {
  const { paddingMm, gapMm, headerMm, planMm, qrMm, credentialQrMm, serialMm, codeHeightMm } = COMPACT_CARD
  const serial = serialLabel(meta.batchNo, card.serialNo, meta.quantity)
  const qrSize = card.password === null ? qrMm : credentialQrMm
  const credentialBox = meta.printQr ? COMPACT_CREDENTIAL_BOX : COMPACT_LOOSE_CREDENTIAL_BOX
  return (
    <div
      style={{
        boxSizing: 'border-box',
        width: `${layout.cardWidthMm}mm`,
        height: `${layout.cardHeightMm}mm`,
        padding: `${paddingMm}mm`,
        display: 'flex',
        flexDirection: 'column',
        gap: `${gapMm}mm`,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: '0.8mm', alignItems: 'center', height: `${headerMm}mm`, flex: 'none' }}>
        {meta.logoUrl ? (
          <img
            src={meta.logoUrl}
            alt=""
            style={{ height: `${headerMm}mm`, maxWidth: '9mm', objectFit: 'contain', flex: 'none' }}
          />
        ) : null}
        <span dir="auto" style={{ ...ellipsis, flex: 1, minWidth: 0, fontWeight: 700, fontSize: '2.2mm', lineHeight: `${headerMm}mm` }}>
          {meta.issuer ?? ''}
        </span>
      </div>

      <span
        dir="auto"
        style={{ ...ellipsis, display: 'block', flex: 'none', height: `${planMm}mm`, lineHeight: `${planMm}mm`, fontWeight: 700, fontSize: '2.4mm' }}
      >
        {meta.planName}
      </span>

      {meta.printQr ? (
        <div style={{ display: 'flex', gap: '1mm', height: `${qrSize}mm`, flex: 'none' }}>
          <QRCodeSVG
            value={qrContent(meta.portalUrl, card)}
            size={256}
            level="M"
            marginSize={2}
            style={{ width: `${qrSize}mm`, height: `${qrSize}mm`, flex: 'none' }}
          />
          <bdi dir="ltr" style={{ ...ellipsis, display: 'block', alignSelf: 'flex-end', minWidth: 0, fontSize: '2.1mm', lineHeight: 1.2 }}>
            {serial}
          </bdi>
        </div>
      ) : (
        <bdi dir="ltr" style={{ ...ellipsis, display: 'block', flex: 'none', height: `${serialMm}mm`, fontSize: '2.1mm', lineHeight: `${serialMm}mm` }}>
          {serial}
        </bdi>
      )}

      {card.password === null ? (
        <CompactCode
          code={card.code}
          box={meta.printQr ? COMPACT_CODE_BOX : COMPACT_LOOSE_CODE_BOX}
          heightMm={meta.printQr ? codeHeightMm : null}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: `${gapMm}mm`, flex: 1, minHeight: 0 }}>
          <CredentialRow
            label={meta.loginLabel}
            value={card.code}
            box={credentialBox}
            labelMm={CREDENTIAL_LABEL.compactMm}
            labelFontMm={2.1}
          />
          <CredentialRow
            label={meta.passwordLabel}
            value={card.password}
            box={credentialBox}
            labelMm={CREDENTIAL_LABEL.compactMm}
            labelFontMm={2.1}
          />
        </div>
      )}
    </div>
  )
}

interface CodeBox {
  widthMm: number
  heightMm: number
  maxFontMm: number
  maxLines: number
}

/** `heightMm: null` — bo'shagan joyni to'liq egallaydi (QR'siz karta) */
function CompactCode({ code, box, heightMm }: { code: string; box: CodeBox; heightMm: number | null }) {
  const fit = fitCode(code, box)
  return (
    <bdi
      dir="ltr"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...(heightMm === null ? { flex: 1, minHeight: 0 } : { flex: 'none', height: `${heightMm}mm` }),
        fontFamily: MONO_FONT,
        fontWeight: 700,
        fontSize: `${fit.fontMm}mm`,
        lineHeight: CODE_LINE_HEIGHT,
        letterSpacing: '0.05em',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {fit.lines.map((line, i) => (
        <span key={i}>{line}</span>
      ))}
    </bdi>
  )
}

interface CredentialRowProps {
  label: string
  value: string
  box: CodeBox
  labelMm: number
  labelFontMm: number
}

function CredentialRow({ label, value, box, labelMm, labelFontMm }: CredentialRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: `${CREDENTIAL_LABEL.gapMm}mm`, height: `${box.heightMm}mm`, flex: 1, minWidth: 0 }}>
      <span dir="auto" style={{ ...ellipsis, flex: 'none', width: `${labelMm}mm`, fontSize: `${labelFontMm}mm`, color: '#333' }}>
        {label}
      </span>
      <bdi
        dir="ltr"
        style={{
          ...ellipsis,
          display: 'block',
          flex: 1,
          minWidth: 0,
          fontFamily: MONO_FONT,
          fontWeight: 700,
          fontSize: `${fitCode(value, box).fontMm}mm`,
          letterSpacing: '0.05em',
          textAlign: 'center',
        }}
      >
        {value}
      </bdi>
    </div>
  )
}
